import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    teamSize: '',
    preferredDate: '',
    workshopType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        companyName: formData.companyName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        teamSize: formData.teamSize,
        preferredDate: formData.preferredDate,
        workshopType: formData.workshopType,
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5nglpppi8ifp4297vbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          teamSize: '',
          preferredDate: '',
          workshopType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://public.readdy.ai/ai/img_res/a4374e89af6cdb90b1a5ff23e80fd2ad.jpg"
          alt="Workshop Environment"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/80"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-6xl md:text-8xl font-bold text-white uppercase tracking-wide mb-8">
            Request Your Proposal
          </h2>
          <p className="text-2xl md:text-3xl text-white leading-relaxed max-w-3xl mx-auto">
            Transform your team with an unforgettable immersive experience. Let's create something extraordinary together.
          </p>
        </div>

        <div className="bg-gradient-to-br from-[#16213E]/95 to-[#0F0F1A]/95 backdrop-blur-md rounded-3xl p-10 md:p-12 border border-cyan-400/30 shadow-2xl">
          <form id="workshop-proposal-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label htmlFor="companyName" className="block text-cyan-400 font-semibold mb-3 text-sm uppercase tracking-wider">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white text-base focus:outline-none focus:border-cyan-400 transition-colors"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="contactName" className="block text-cyan-400 font-semibold mb-3 text-sm uppercase tracking-wider">
                  Contact Name *
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white text-base focus:outline-none focus:border-cyan-400 transition-colors"
                  placeholder="Your Full Name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-cyan-400 font-semibold mb-3 text-sm uppercase tracking-wider">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white text-base focus:outline-none focus:border-cyan-400 transition-colors"
                  placeholder="your.email@company.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-cyan-400 font-semibold mb-3 text-sm uppercase tracking-wider">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white text-base focus:outline-none focus:border-cyan-400 transition-colors"
                  placeholder="+65 XXXX XXXX"
                />
              </div>

              <div>
                <label htmlFor="teamSize" className="block text-cyan-400 font-semibold mb-3 text-sm uppercase tracking-wider">
                  Team Size *
                </label>
                <select
                  id="teamSize"
                  name="teamSize"
                  value={formData.teamSize}
                  onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                  required
                  className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white text-base focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer"
                >
                  <option value="">Select team size</option>
                  <option value="10-20">10-20 participants</option>
                  <option value="20-50">20-50 participants</option>
                  <option value="50-100">50-100 participants</option>
                  <option value="100+">100+ participants</option>
                </select>
              </div>

              <div>
                <label htmlFor="preferredDate" className="block text-cyan-400 font-semibold mb-3 text-sm uppercase tracking-wider">
                  Preferred Date
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                  className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white text-base focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer"
                />
              </div>
            </div>

            <div>
              <label htmlFor="workshopType" className="block text-cyan-400 font-semibold mb-3 text-sm uppercase tracking-wider">
                Workshop Type *
              </label>
              <select
                id="workshopType"
                name="workshopType"
                value={formData.workshopType}
                onChange={(e) => setFormData({ ...formData, workshopType: e.target.value })}
                required
                className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white text-base focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer"
              >
                <option value="">Select workshop type</option>
                <option value="half-day">Half-Day Workshop (4 hours)</option>
                <option value="full-day">Full-Day Experience (8 hours)</option>
                <option value="multi-day">Multi-Day Program (2-3 days)</option>
                <option value="weekly-series">Weekly Series (4-6 sessions)</option>
                <option value="custom">Custom Program</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-cyan-400 font-semibold mb-3 text-sm uppercase tracking-wider">
                Tell Us About Your Team &amp; Goals
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
                rows={5}
                maxLength={500}
                className="w-full px-6 py-4 bg-black/50 border border-white/20 rounded-xl text-white text-base focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                placeholder="Share your team's goals, challenges, or any specific requirements..."
              ></textarea>
              <div className="text-right text-gray-400 text-sm mt-2">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-6 text-center">
                <i className="ri-checkbox-circle-line text-green-400 text-4xl mb-3"></i>
                <p className="text-green-400 font-semibold text-lg">
                  Thank you! We'll get back to you within 24 hours with your custom proposal.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-6 text-center">
                <i className="ri-error-warning-line text-red-400 text-4xl mb-3"></i>
                <p className="text-red-400 font-semibold text-lg">
                  Something went wrong. Please try again or contact us directly.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="relative w-full md:w-auto mx-auto flex items-center justify-center gap-4 px-16 py-5 bg-black rounded-full text-white text-xl font-bold uppercase tracking-wider hover:shadow-2xl hover:shadow-orange-500/50 transition-all hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer group whitespace-nowrap"
            >
              <div className="absolute -left-2 w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center group-hover:rotate-180 transition-transform duration-500">
                <i className="ri-file-text-line text-white text-2xl"></i>
              </div>
              <span className="ml-12">{isSubmitting ? 'Sending...' : 'Request Proposal'}</span>
              <i className="ri-arrow-right-up-line text-2xl"></i>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
